import {
  ActionType,
  BudgetType,
  RunningPattern,
} from 'core/rtbCampaign/singlePage/common';
import { useFormik } from 'formik';
import moment from 'moment';
import { useCampaignStore } from 'store/campaignStore';

interface useAlwaysOnBudgetManagementProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

const useAlwaysOnBudgetManagement = (
  initialState: useAlwaysOnBudgetManagementProps = {
    defaultForm: {},
    values: {},
    setFieldValue: async () => Promise.resolve(),
  },
) => {
  const { defaultForm, values, setFieldValue } = initialState;
  const { actionType } = useCampaignStore();

  // 變更預算類型
  const handleBudgetTypeChange = (newBudgetType) => {
    setFieldValue('basic.budgetType', newBudgetType);
    handleResetAllBudget();
  };

  // 變更預算（always on）
  const handleAlwaysOnBudgetChange = (newBudget) => {
    const { type, newPrice } = newBudget;
    if (type === BudgetType.DAILY) {
      setFieldValue('basic.dailyTargetBudget', newPrice);
      // 編輯模式下日預算模式有異動日預算才執行 handleAutoSetBudget
      if (
        actionType === ActionType.EDIT &&
        defaultForm.basic.runningPattern === values.basic.runningPattern &&
        defaultForm.basic.budgetType === values.basic.budgetType &&
        Number(newPrice) === defaultForm.basic.dailyTargetBudget
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAlwaysOnAutoSetBudget(newPrice);
      }
    } else if (type === BudgetType.TOTAL) {
      setFieldValue('basic.budget', newPrice);
      handleAutoSetDailyBudget(newPrice);
    }
  };

  // 自動設定每日預算
  const handleAutoSetDailyBudget = (newBudget) => {
    if (values.basic.budgetType === BudgetType.TOTAL) {
      setFieldValue('basic.dailyTargetBudget', Number(newBudget).toFixed(2));
    }
  };

  // 自動設定總預算（always on）
  const handleAlwaysOnAutoSetBudget = (newDailyBudget) => {
    const { basic } = values;
    const start = moment(basic.startDate);
    const end = moment(basic.endDate);
    const today = moment();

    // 根據狀態選擇計算天數的基準
    const baseDate =
      actionType === ActionType.CREATE
        ? start
        : start.isAfter(today)
          ? start
          : today;

    // +1 是因為同日也算 1 天
    const days = end.diff(baseDate, 'days') + 1;

    const { runningPattern, budgetType, expectedSpent } = values.basic;
    if (budgetType === BudgetType.DAILY) {
      const daily = Number(newDailyBudget) || 0;
      const added =
        runningPattern === RunningPattern.LIMITED ? daily * days : daily;
      if (runningPattern === RunningPattern.LIMITED) {
        const total =
          Math.round((expectedSpent + added + Number.EPSILON) * 100) / 100;
        setFieldValue('basic.budget', total.toFixed(2));
        return;
      } else {
        const calcBudget =
          defaultForm.basic.runningPattern === RunningPattern.UNLIMITED
            ? defaultForm.basic.budget +
              (added - defaultForm.basic.dailyTargetBudget)
            : defaultForm.basic.expectedSpent + added;

        const total = Math.round((calcBudget + Number.EPSILON) * 100) / 100;

        setFieldValue('basic.budget', total.toFixed(2));
        return;
      }
    }
  };

  // 重置所有預算
  const handleResetAllBudget = () => {
    setFieldValue('basic.budget', '0');
    setFieldValue('basic.dailyTargetBudget', '0');
  };

  return {
    handleBudgetTypeChange,
    handleAlwaysOnBudgetChange,
    handleAutoSetDailyBudget,
    handleAlwaysOnAutoSetBudget,
    handleResetAllBudget,
  };
};

export default useAlwaysOnBudgetManagement;
